import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UtensilsCrossed, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

// Fix #9: estilo repetido extraído para constante
const oswaldStyle = { fontFamily: "Oswald, sans-serif" };

// Fix #2: máximo de tentativas antes de bloquear o botão
const MAX_FAIL_ATTEMPTS = 5;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation(); // Fix #6: captura rota de origem
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [failCount, setFailCount] = useState(0); // Fix #2: contador de falhas
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  // Fix #5: AbortController para cancelar requisição se componente desmontar
  const abortRef = useRef(null);
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Fix #2: bloqueia após N tentativas falhas
    if (failCount >= MAX_FAIL_ATTEMPTS) {
      setError("Muitas tentativas sem sucesso. Aguarde antes de tentar novamente.");
      return;
    }

    setLoading(true);
    setError("");

    abortRef.current = new AbortController();

    try {
      const res = await api.post("/auth/login", { ...loginForm, keep_logged_in: keepLoggedIn }, {
        signal: abortRef.current.signal, // Fix #5: sinal de cancelamento
      });

      // Define onde salvar baseado no checkbox "Mantenha-me conectado"
      const storage = keepLoggedIn ? localStorage : sessionStorage;

      // Limpa o outro storage por segurança para não haver conflitos
      if (keepLoggedIn) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }

      storage.setItem("token", res.data.token);
      storage.setItem("user", JSON.stringify(res.data.user));

      setFailCount(0);

      // Fix #6: redireciona para rota de origem ou dashboard
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      // Fix #5: ignora erro de requisição cancelada (componente desmontado)
      if (err.name === "CanceledError" || err.name === "AbortError") return;
      setFailCount((prev) => prev + 1);
      setError(err.response?.data?.detail || "Credenciais inválidas. Acesso negado.");
    } finally {
      setLoading(false);
    }
  };

  const isBlocked = failCount >= MAX_FAIL_ATTEMPTS;

  return (
    <div className="min-h-screen flex noise-bg relative">
      {/* Background image — Fix #3: usar imagem local em /public/bg-login.jpg */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/bg-login.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col lg:flex-row w-full">
        {/* Left - Branding */}
        <div className="hidden lg:flex flex-col justify-center items-start px-16 xl:px-24 w-1/2">
          <div className="flex items-center gap-4 mb-8 animate-fade-in-up stagger-1">
            <img
              src="/logo.png"
              alt="Logo SuperPizza"
              className="w-20 h-20 object-contain"
            />
            <div>
              <h1 className="text-5xl font-bold text-white tracking-tight uppercase" style={oswaldStyle}>
                SuperPizza
              </h1>
              <p className="text-sm tracking-[0.3em] text-primary uppercase mt-1">
                CRM Pizzaria
              </p>
            </div>
          </div>
          <p className="text-lg text-white/70 max-w-md leading-relaxed animate-fade-in-up stagger-2">
            Sistema de uso restrito. Apenas funcionários autorizados possuem acesso ao painel operacional.
          </p>
          <div className="flex gap-8 mt-12 animate-fade-in-up stagger-3">
            <div className="flex items-center gap-2 text-primary">
              <ShieldCheck className="w-8 h-8" />
              <div>
                <p className="text-xl font-bold uppercase" style={oswaldStyle}>Ambiente Seguro</p>
                <p className="text-xs text-white/50 uppercase tracking-wider">Acesso Monitorado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Form */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <UtensilsCrossed className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white uppercase" style={oswaldStyle}>
                SuperPizza
              </h1>
            </div>

            <div className="bg-card/90 backdrop-blur-xl border border-border rounded-lg p-8 card-shadow animate-fade-in-up stagger-1">
              <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-foreground uppercase" style={oswaldStyle}>Acesso Restrito</h2>
                <p className="text-sm text-muted-foreground mt-1">Insira suas credenciais para continuar</p>
              </div>

              {error && (
                <div data-testid="auth-error" className="mb-6 p-3 rounded-md bg-destructive/15 border border-destructive/30 text-destructive text-sm text-center font-medium">
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label htmlFor="login-email" className="text-sm text-muted-foreground mb-1.5 block">Email de Acesso</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="admin@superpizza.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="bg-input border-transparent focus:border-primary h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-pass" className="text-sm text-muted-foreground mb-1.5 block">Senha</Label>
                  <div className="relative">
                    <Input
                      id="login-pass"
                      type={showPass ? "text" : "password"}
                      placeholder="********"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="bg-input border-transparent focus:border-primary h-11 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Fix #8: Checkbox da design system em vez do nativo */}
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="keep-logged"
                    checked={keepLoggedIn}
                    onCheckedChange={setKeepLoggedIn}
                  />
                  <Label htmlFor="keep-logged" className="text-sm text-muted-foreground cursor-pointer font-normal">
                    Mantenha-me conectado
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || isBlocked}
                  className="w-full h-11 mt-2 bg-primary hover:bg-primary/90 text-white font-medium shadow-lg shadow-primary/20 gap-2"
                >
                  {loading ? "Autenticando..." : isBlocked ? "Acesso bloqueado" : "Entrar no Sistema"}
                  {!loading && !isBlocked && <ArrowRight className="w-4 h-4" />}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
